/* eslint-disable no-nested-ternary */
import { Box } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import TablePagination from '@mui/material/TablePagination';
import {
  CellContext,
  ColumnFiltersState,
  Row,
  SortingState,
  Table as TableType,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { KeyboardEvent, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Checkbox from '../../Checkbox/Checkbox';
import inputStyles from '../../Input/Input.module.css';
import Tooltip from '../../Tooltip/Tooltip';
import sharedTableStyles from '../SharedTableStyles.module.css';
import SortIcon from '../SortIcon';
import styles from './TableCard.module.css';
import { TableCardProps } from './TableCard.types';
import { useTranslation } from 'react-i18next';
import { TableCardTitle } from './TableCardTitle/TableCardTitle';

const TableCard = <TData extends object>({
  breadcrumb,
  data,
  columns,
  returnedRecordsText,
  enableFilters = false,
  enableRowSelection = false,
  enableMultiRowSelection = true,
  noRowsMessage,
  title,
  className,
  selectedRows,
  onRowSelectionChange,
  isLoading,
  width,
  itemIdentifier = 'none',
  includeMinWidth = true,
  accessTableApi,
  showPagination = false,

  bundleLibraryCaption = false,
  meta,
}: TableCardProps<TData>) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [rowSelection, setRowSelection] = useState({});
  const id = useId();
  const filterDebounceRefs = useRef<Record<string, number | undefined>>({});
  const [filterInputValues, setFilterInputValues] = useState<Record<string, string>>({});

  // if we have an onRowSelectionChange prop, we assume selection state is being managed externally (via the selectedRows prop)
  const getRowSelectionHandler = () => {
    if (onRowSelectionChange) return onRowSelectionChange;
    return setRowSelection;
  };

  // if we receive selectedRows from props, update our internal state
  useEffect(() => {
    if (selectedRows) {
      setRowSelection(selectedRows);
    }
  }, [selectedRows]);

  const LoadingSkeleton = useCallback(
    ({ column }: CellContext<TData, unknown>) =>
      column.id === 'id' ? <div /> : <div className={sharedTableStyles.skeleton} data-testid="loading-skeleton-cell" />,
    [],
  );

  const renderCheckboxCell = useCallback(
    (params: { row: Row<unknown> }) => (
      <div className={sharedTableStyles.rowCheckbox}>
        <Checkbox
          label=""
          aria-label="select row"
          id={`${id}-row-${params.row.id}-checkbox`}
          checked={params.row.getIsSelected()}
          onChange={params.row.getToggleSelectedHandler()}
          disabled={!params.row.getCanSelect()}
        />
      </div>
    ),
    [id],
  );

  const renderHeaderCell = useCallback(
    (params: { table: TableType<unknown> }) => (
      <div className={sharedTableStyles.headerCheckbox}>
        <Checkbox
          label=""
          aria-label="select all rows"
          id={`${id}-header-checkbox`}
          checked={params.table.getIsAllRowsSelected()}
          onChange={params.table.getToggleAllRowsSelectedHandler()}
        />
      </div>
    ),
    [id],
  );

  const columnsWithRowSelection = useMemo(
    () => [
      {
        id: 'select',
        header: enableMultiRowSelection ? renderHeaderCell : undefined,
        cell: renderCheckboxCell,
      },
      ...columns,
    ],
    [columns, enableMultiRowSelection, renderCheckboxCell, renderHeaderCell],
  );

  const tableData = useMemo(() => (isLoading ? Array(30).fill({}) : data || []), [isLoading, data]);

  const tableColumns = useMemo(() => {
    if (isLoading) {
      return columns.map((column) => ({
        ...column,
        cell: LoadingSkeleton,
      }));
    }
    return enableRowSelection ? columnsWithRowSelection : columns;
  }, [isLoading, enableRowSelection, columnsWithRowSelection, columns, LoadingSkeleton]);

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    enableFilters,
    enableRowSelection,
    enableMultiRowSelection,
    onColumnFiltersChange: setColumnFilters,

    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },

    initialState: {
      pagination: showPagination
        ? {
            pageIndex: 0,
            pageSize: 5,
          }
        : {},
    },
    onSortingChange: setSorting,
    onRowSelectionChange: getRowSelectionHandler(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: showPagination ? getPaginationRowModel() : undefined,
    meta,
    debugTable: false,
  });

  useEffect(() => {
    if (accessTableApi) accessTableApi(table);
  }, [table, accessTableApi]);

  const getAriaSort = (sortStatus: 'asc' | 'desc' | false) => {
    switch (sortStatus) {
      case 'asc':
        return 'ascending';
      case 'desc':
        return 'descending';
      default:
        return 'none';
    }
  };

  const filteredRowCount = table.getFilteredRowModel().rows.length;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = useState(false);
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);

  const recordText = useMemo(() => {
    if (returnedRecordsText) {
      return returnedRecordsText(filteredRowCount);
    }
    return t('common.table.row', { count: filteredRowCount });
  }, [returnedRecordsText, filteredRowCount, t]);

  const checkForScrollbar = useCallback(() => {
    if (!tableContainerRef.current) return;
    if (tableContainerRef.current.clientWidth === tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(false);
    }
    if (tableContainerRef.current.clientWidth < tableContainerRef.current.scrollWidth) {
      setHasHorizontalScrollbar(true);
    }
    if (tableContainerRef.current.clientHeight === tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(false);
    }
    if (tableContainerRef.current.clientHeight < tableContainerRef.current.scrollHeight) {
      setHasVerticalScrollbar(true);
    }
  }, []);

  useLayoutEffect(() => {
    checkForScrollbar();
  }, [isLoading, checkForScrollbar]);

  useEffect(() => {
    window.addEventListener('resize', checkForScrollbar);
    return () => window.removeEventListener('resize', checkForScrollbar);
  }, [checkForScrollbar]);

  return (
    <div
      style={{ width }}
      className={classNames(styles.card, {
        [className!]: className !== undefined,
        [styles.cardFilled]: showPagination,
        [sharedTableStyles.minWidth]: includeMinWidth,
      })}
    >
      <div
        ref={tableContainerRef}
        className={classNames(styles.tableContainer, {
          [styles.noTitle]: title === undefined,
          [styles.loading]: isLoading,
          [styles.tableFilled]: showPagination,
          [styles.withVerticalScrollBar]: hasVerticalScrollbar,
        })}
      >
        <table className={classNames(styles.table, { [styles.withRowSelection]: enableRowSelection })}>
          {}
          {title === 'All Bundles' ? (
            <caption
              className={classNames(styles.tableTitle, {
                [styles.noTitleCaption]: title === undefined,
                [styles.noFiltersCaption]: !enableFilters && title === undefined,
              })}
              style={!enableFilters && title === undefined ? { width: `calc(${width} - 82px)` } : undefined}
            >
              <TableCardTitle breadcrumb={breadcrumb} title={title} data={undefined} columns={[]} />
            </caption>
          ) : (
            <caption
              className={classNames(styles.tableTitle, {
                [styles.noTitleCaption]: title === undefined,
                [styles.noFiltersCaption]: !enableFilters && title === undefined,
              })}
              style={!enableFilters && title === undefined ? { width: `calc(${width} - 82px)` } : undefined}
            >
              {title}
            </caption>
          )}
          <thead className={styles.tableHead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <th
                    data-testid={`table-header-${header.id}`}
                    key={header.id}
                    className={classNames(sharedTableStyles.columnHeader, sharedTableStyles.stickHeader, {
                      [styles.checkboxColumnHeader]: enableRowSelection && index === 0,
                    })}
                    aria-sort={getAriaSort(header.column.getIsSorted())}
                  >
                    <div
                      data-testid={`table-sort-button-${header.id}`}
                      className={sharedTableStyles.columnHeaderTextAndIcon}
                      tabIndex={header.column.columnDef.enableSorting ? 0 : undefined}
                      aria-label={`sort column ${
                        typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : ''
                      }`}
                      onClick={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      onTouchStart={
                        header.column.columnDef.enableSorting ? header.column.getToggleSortingHandler() : undefined
                      }
                      role="button"
                      onKeyDown={(event: KeyboardEvent) => {
                        switch (event.key) {
                          case ' ':
                          case 'Enter':
                            header.column.toggleSorting();
                            break;
                          default:
                            break;
                        }
                      }}
                    >
                      <span className={sharedTableStyles.columnHeaderText}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.columnDef.enableSorting && <SortIcon />}
                      {header.column.columnDef.meta?.tooltipText !== undefined && (
                        <Tooltip content={header.column.columnDef.meta?.tooltipText} size={16} />
                      )}
                    </div>
                    {header.column.getCanFilter() ? (
                      <input
                        disabled={header.column.columnDef.meta?.disableFilter}
                        onChange={(event) => {
                          const value = event.target.value;
                          const key = header.id;
                          setFilterInputValues((prev) => ({ ...prev, [key]: value }));
                          const existing = filterDebounceRefs.current[key];
                          if (existing) window.clearTimeout(existing);
                          filterDebounceRefs.current[key] = window.setTimeout(() => {
                            header.column.setFilterValue(value);
                          }, 100);
                        }}
                        value={
                          filterInputValues[header.id] !== undefined
                            ? filterInputValues[header.id]
                            : (header.column.getFilterValue() as string) ?? ''
                        }
                        className={classNames(inputStyles.input, sharedTableStyles.headerFilter)}
                        aria-label={`${header.column.columnDef.header} filter input`}
                        id={`${header.id}filterInput`}
                      />
                    ) : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, index) => (
              <tr
                key={row.original[itemIdentifier] || row.id}
                className={classNames(sharedTableStyles.row, {
                  [sharedTableStyles.even]: index % 2 === 0,
                  [sharedTableStyles.selectedRow]: row.getIsSelected(),
                })}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className={sharedTableStyles.cell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {!isLoading && filteredRowCount === 0 && (
            <caption className={styles.noRowsMessage}>{noRowsMessage ?? t('common.table.noData')}</caption>
          )}
        </table>
      </div>
      <div className={showPagination ? styles.footerWithPagination : styles.footer}>
        {!isLoading && (
          <div>
            {showPagination ? (
              <Box pr={4}>
                <TablePagination
                  component="div"
                  size="small"
                  count={data?.length || 0}
                  page={table.getState().pagination.pageIndex}
                  onPageChange={(e, page) => table.setPageIndex(page)}
                  rowsPerPage={table.getState().pagination.pageSize || 5}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
                  sx={{
                    '.MuiTablePagination-actions button svg': {
                      height: '24px',
                      width: '24px',
                    },
                    '.MuiToolbar-root .MuiSelect-select': {
                      fontFamily: 'Open Sans',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      fontSize: '2rem',
                      lineHeight: '24px',
                    },
                  }}
                />
              </Box>
            ) : (
              <div className={bundleLibraryCaption ? styles.footerContainer : styles.halfWidthContent}>
                {bundleLibraryCaption && (
                  <div className={styles.leftContent}>
                    <div className={styles.linkedBundleContainer}>
                      <LinkIcon style={{ fontSize: '30px', color: '#266DCC' }} />
                      <p className={styles.linkedBundlesLabel}>{t('common.linkedBundles')}</p>
                    </div>
                  </div>
                )}
                <h2 className={styles.rightContent}>{`${filteredRowCount} ${recordText}`}</h2>
              </div>
            )}
          </div>
        )}
      </div>
      {hasHorizontalScrollbar && <div className={sharedTableStyles.scrollShadow} />}
    </div>
  );
};

export default TableCard;
